<script setup lang="ts">
  import {
    BottomSheet,
    Button,
    ButtonAppearance,
    ButtonSize,
    Icon,
    IconSize,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { ref } from 'vue';
  import { SERP_FEATURES_SHEET_TEXTS } from '../config/texts';
  import type { SerpCardFeature } from '../lib/serpCardFeatures';

  interface SerpFeaturesSheetProps {
    features: SerpCardFeature[];
  }

  defineProps<SerpFeaturesSheetProps>();

  interface SerpFeaturesSheetEmits {
    (e: 'close'): void;
  }

  const emit = defineEmits<SerpFeaturesSheetEmits>();

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  // Закрываем через сам BottomSheet, а не голым emit, чтобы шторка уехала с анимацией.
  const onClose = async (): Promise<void> => {
    await bottomSheetRef.value?.close();
  };
</script>

<template>
  <div
    class="serp-features-sheet"
    data-testid="serp-features-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="serp-features-sheet__sheet"
      :animated="true"
      :icon-closable="true"
      @close="emit('close')"
    >
      <template #header>
        <Typo
          class="serp-features-sheet__title"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="serp-features-sheet-title"
        >
          {{ SERP_FEATURES_SHEET_TEXTS.title }}
        </Typo>
      </template>

      <ul
        class="serp-features-sheet__features"
        data-testid="serp-features-sheet-features"
      >
        <li
          v-for="feature in features"
          :key="feature.id"
          class="serp-features-sheet__feature"
          :data-testid="`serp-features-sheet-feature-${feature.id}`"
        >
          <Icon
            class="serp-features-sheet__feature-icon"
            :name="feature.icon"
            :size="IconSize.M"
          />
          <Typo
            class="serp-features-sheet__feature-label"
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ feature.label }}
          </Typo>
        </li>
      </ul>

      <template #footer>
        <Button
          class="serp-features-sheet__close"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.L"
          data-testid="serp-features-sheet-close"
          @click="onClose"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ SERP_FEATURES_SHEET_TEXTS.close }}
          </Typo>
        </Button>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .serp-features-sheet {
    // Сам BottomSheet телепортируется в body, а на месте остаётся только скрытая обёртка. Без display: contents
    // она была бы flex-элементом карточки и на каждое открытие шторки добавляла ей лишний gap.
    display: contents;

    &__title {
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }

    &__features {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    &__feature {
      align-items: center;
      box-sizing: border-box;
      display: flex;
      gap: var(--spacing-xs);
      min-height: 24px;
      padding: var(--spacing-xxxs) 0;
    }

    &__feature-icon {
      // color — для контурных иконок (stroke="currentColor"), fill — для заливочных: без обоих спрайт
      // берёт серый цвет от .tl-bottom-sheet__content либо чёрный по умолчанию.
      color: var(--color-text-primary);
      fill: var(--color-text-primary);
      flex-shrink: 0;
    }

    &__feature-label {
      color: var(--color-text-primary);
    }

    &__close {
      width: 100%;
    }
  }
</style>
